import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

import { logger } from "utils/logger";
import { VideoPlayerContainer, VideoTitle, VideoWrap } from "./VideoPlayer.styles";

const WATCH_THRESHOLD_RATIO = 0.3;

export interface HlsVideoPlayerProps {
  playlistUrl: string;
  videoName?: string;
  requestHeaders?: Record<string, string>;
  onVideoRef?: (ref: HTMLVideoElement | null) => void;
  onPlay?: () => void;
  onWatchThresholdReached?: () => void;
  className?: string;
  controlsList?: string;
}

const HlsVideoPlayer: React.FC<HlsVideoPlayerProps> = ({
  playlistUrl,
  videoName,
  requestHeaders,
  onVideoRef,
  onPlay,
  onWatchThresholdReached,
  className,
  controlsList = "nodownload noremoteplayback",
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoWrapRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTitle, setShowTitle] = useState(false);
  const hideTitleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    setError(null);

    if (!video || !playlistUrl) {
      logger.error("HlsVideoPlayer: video element or playlistUrl отсутствует", {
        video: Boolean(video),
        playlistUrl,
      });
      return;
    }

    if (Hls.isSupported()) {
      const hls = new Hls(
        requestHeaders && Object.keys(requestHeaders).length
          ? {
              xhrSetup: (xhr) => {
                Object.entries(requestHeaders).forEach(([key, value]) => {
                  xhr.setRequestHeader(key, value);
                });
              },
            }
          : undefined,
      );

      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(playlistUrl));
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data?.fatal) return;

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          logger.warn("HlsVideoPlayer: повторная загрузка после сетевой ошибки", data);
          hls.startLoad();
          return;
        }

        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          logger.warn("HlsVideoPlayer: восстановление после media error", data);
          hls.recoverMediaError();
          return;
        }

        if (data.response?.code === 401) {
          setError("Ошибка авторизации. Проверьте доступ к видео.");
        } else if (data.response?.code === 403) {
          setError("Доступ к видео запрещён.");
        } else if (data.response?.code === 404) {
          setError("Видео не найдено.");
        } else {
          setError("Ошибка воспроизведения видео.");
        }
        hls.destroy();
      });

      return () => hls.destroy();
    }

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      if (requestHeaders && Object.keys(requestHeaders).length) {
        logger.warn(
          "HlsVideoPlayer: native HLS не поддерживает произвольные request headers",
        );
      }
      video.src = playlistUrl;
      const handleError = () => setError("Ошибка воспроизведения видео.");
      video.addEventListener("error", handleError);
      return () => {
        video.removeEventListener("error", handleError);
        video.removeAttribute("src");
        video.load();
      };
    }

    setError("HLS не поддерживается вашим браузером.");
  }, [playlistUrl, requestHeaders]);

  useEffect(() => {
    const videoWrap = videoWrapRef.current;
    if (!videoWrap || !videoName) return;

    const resetHideTimer = () => {
      if (hideTitleTimerRef.current) clearTimeout(hideTitleTimerRef.current);
      setShowTitle(true);
      hideTitleTimerRef.current = setTimeout(() => setShowTitle(false), 3000);
    };
    const handleMouseLeave = () => {
      if (hideTitleTimerRef.current) clearTimeout(hideTitleTimerRef.current);
      setShowTitle(false);
    };

    videoWrap.addEventListener("mouseenter", resetHideTimer);
    videoWrap.addEventListener("mousemove", resetHideTimer);
    videoWrap.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      videoWrap.removeEventListener("mouseenter", resetHideTimer);
      videoWrap.removeEventListener("mousemove", resetHideTimer);
      videoWrap.removeEventListener("mouseleave", handleMouseLeave);
      if (hideTitleTimerRef.current) clearTimeout(hideTitleTimerRef.current);
    };
  }, [videoName]);

  useEffect(() => {
    if (!onVideoRef) return;
    const updateRef = () => onVideoRef(videoRef.current);
    updateRef();
    const timeoutId = setTimeout(updateRef, 100);
    return () => {
      clearTimeout(timeoutId);
      onVideoRef(null);
    };
  }, [onVideoRef, playlistUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !onPlay) return;

    const handlePlay = () => {
      setTimeout(() => {
        if (!video.paused) onPlay();
      }, 50);
    };
    video.addEventListener("play", handlePlay);
    video.addEventListener("playing", handlePlay);
    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("playing", handlePlay);
    };
  }, [onPlay]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !onWatchThresholdReached) return;

    let watchedSeconds = 0;
    let startedAt: number | null = null;
    let reported = false;

    const accumulateAndMaybeReport = () => {
      if (reported) return;
      const now = performance.now();
      if (startedAt !== null) {
        watchedSeconds += Math.max(0, (now - startedAt) / 1000);
        startedAt = now;
      }
      if (
        Number.isFinite(video.duration) &&
        video.duration > 0 &&
        watchedSeconds >= video.duration * WATCH_THRESHOLD_RATIO
      ) {
        reported = true;
        startedAt = null;
        onWatchThresholdReached();
      }
    };

    const handlePlaying = () => {
      if (!reported && startedAt === null) startedAt = performance.now();
    };
    const handleStop = () => {
      accumulateAndMaybeReport();
      startedAt = null;
    };
    const handleSeeked = () => {
      if (!reported && !video.paused && !video.ended) startedAt = performance.now();
    };

    video.addEventListener("playing", handlePlaying);
    video.addEventListener("timeupdate", accumulateAndMaybeReport);
    video.addEventListener("pause", handleStop);
    video.addEventListener("waiting", handleStop);
    video.addEventListener("seeking", handleStop);
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("ended", handleStop);
    return () => {
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("timeupdate", accumulateAndMaybeReport);
      video.removeEventListener("pause", handleStop);
      video.removeEventListener("waiting", handleStop);
      video.removeEventListener("seeking", handleStop);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("ended", handleStop);
    };
  }, [onWatchThresholdReached, playlistUrl]);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (error) {
    return (
      <VideoPlayerContainer className={className}>
        <div style={{ padding: 16, color: "#fff", textAlign: "center" }}>
          {error}
        </div>
      </VideoPlayerContainer>
    );
  }

  return (
    <VideoPlayerContainer className={className}>
      <VideoWrap ref={videoWrapRef} onContextMenu={(event) => event.preventDefault()}>
        <video
          ref={videoRef}
          controls
          playsInline={isMobile}
          preload="metadata"
          controlsList={controlsList}
        />
        {videoName ? <VideoTitle $show={showTitle}>{videoName}</VideoTitle> : null}
      </VideoWrap>
    </VideoPlayerContainer>
  );
};

export default HlsVideoPlayer;